import { localization } from "@/plugins/i18n"
import { ts } from "@/plugins/i18n-formatted"
import EventBoat from "@model/EventBoat"
import EventClass from "@model/EventClass"
import { Model, useRepo } from "pinia-orm"
import { BelongsTo, Num, Str, Uid } from "pinia-orm/decorators"

export default class EventCategory extends Model {
    static entity = 'events-categories'

    @Uid() declare id: string
    @Uid() declare eventClassId: string
    @BelongsTo( () => EventClass, 'eventClassId' ) declare eventClass: EventClass
    @Str( null ) declare title: string
    @Str( null ) declare shortcut: string
    @Str( null ) declare sex: string
    @Num( null ) declare ageUnder: number
    @Num( null ) declare ageAbove: number
    @Str( null ) declare otherLimit: string

    get dump () {
        return {
            id: this.id,
            title: this.title,
            shortcut: this.shortcut,
            sex: this.sex,
            ageUnder: this.ageUnder,
            ageAbove: this.ageAbove,
            otherLimit: this.otherLimit
        }
    }

    get visibleAge () {
        return ( this.ageAbove !== null && this.ageUnder !== null )
            ? ts( 'bothAge', { under: this.ageUnder, above: this.ageAbove } )
            : ( this.ageAbove !== null
                    ? ts( 'aboveAge', { age: this.ageAbove } )
                    : ( this.ageUnder !== null
                            ? ts( 'underAge', { age: this.ageUnder } )
                            : ts( 'noLimits' )
                    )
            )
    }

    get visibleSex () {
        return ts( this.sex === null ? 'sexLimit-all' : 'sexLimit-' + this.sex )
    }

    get visibleShortcut () {
        if ( this.shortcut === null || this.shortcut === undefined ) {
            let generic = ''

            if ( this.ageUnder !== null ) {
                generic += 'U' + ( this.ageUnder + 1 )
            }
            else if ( this.ageAbove !== null ) {
                generic += 'O' + this.ageAbove
            }

            if ( this.sex !== null )
                generic += ts( 'categoryShortcut-' + this.sex )

            if ( this.otherLimit !== null )
                generic += ts( 'categoryShortcut-' + this.otherLimit )

            return generic.trim()
        }

        return this.shortcut
    }

    get fileName () {
        if ( this.shortcut === null || this.shortcut === undefined )
            return this.visibleShortcut.trim().replaceAll( ' ', '-' )

        return this.shortcut.toAscii().replaceAll( ' ', '-' )
    }

    get explanation () {
        return ( this.sex === null ? ts( 'sexLimit-none' ) : this.visibleSex ) + ", " + this.visibleAge + ", " + this.visibleOtherLimit
    }

    get visibleTitle () {
        if ( this.title === null || this.title === undefined ) {
            let generic = ts( 'categoryFull-' + ( this.otherLimit === null ? 'all' : this.otherLimit ) )

            if ( this.sex !== null )
                generic += ' ' + ts( 'categoryFull-' + this.sex )

            if ( this.ageAbove !== null ) {
                generic += ' ' + ts( 'aboveAge', { age: this.ageAbove } )
            }

            if ( this.ageUnder !== null ) {
                generic += ' ' + ts( 'underAge', { age: this.ageUnder } )
            }

            return generic.trim()
        }

        return this.title
    }

    get visibleShortcutCz () {
        const origLocale = localization.global.locale.value

        localization.global.locale.value = 'cs'

        const result = this.visibleShortcut

        localization.global.locale.value = origLocale

        return result
    }

    get visibleShortcutEn () {
        const origLocale = localization.global.locale.value

        localization.global.locale.value = 'en'

        const result = this.visibleShortcut

        localization.global.locale.value = origLocale

        return result
    }

    get visibleTitleCz () {
        const origLocale = localization.global.locale.value

        localization.global.locale.value = 'cs'

        const result = this.visibleTitle

        localization.global.locale.value = origLocale

        return result
    }

    get visibleTitleEn () {
        const origLocale = localization.global.locale.value

        localization.global.locale.value = 'en'

        const result = this.visibleTitle

        localization.global.locale.value = origLocale

        return result
    }

    get visibleOtherLimit () {
        return ts( this.otherLimit === null ? 'otherLimit-all' : 'otherLimit-' + this.otherLimit )
    }

    get boats () {
        return useRepo( EventBoat ).where( 'eventId', this.eventClass.eventId ).where( 'boatClassId', this.eventClass.boatClassId ).withAllRecursive().get().filter( ( boat ) => this.isBoatInCategory( boat ) ).length
    }

    isBoatInCategory ( boat ) {
        if ( boat.overrideCategories )
            return boat.manualCategories.some( boatCategory => boatCategory.eventCategoryId === this.id )

        if ( this.sex !== null ) {
            const boatSex = boat.sex

            if ( boatSex === 'male' && ( this.sex === 'female' || this.sex === 'mix' || this.sex === 'female+mix' ) )
                return false

            if ( boatSex === 'female' && ( this.sex === 'male' || this.sex === 'mix' || this.sex === 'male+mix' ) )
                return false

            if ( boatSex === 'mix' && ( this.sex === 'male' || this.sex === 'female' ) )
                return false
        }

        if ( this.ageUnder !== null && boat.ageOldest > this.ageUnder )
            return false

        if ( this.ageAbove !== null && boat.ageYoungest < this.ageAbove )
            return false

        if ( this.otherLimit === 'cze' && !boat.isCzech )
            return false

        if ( this.otherLimit === 'region' && this.eventClass.event.clubId !== undefined && !boat.isFromRegion( this.eventClass.event.clubId ) )
            return false

        if ( this.otherLimit === 'district' && this.eventClass.event.clubId !== undefined && !boat.isFromDistrict( this.eventClass.event.clubId ) )
            return false

        return true
    }

    static piniaOptions = {
        persist: true
    }
}